import React, {
  Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  View, StyleSheet, Text, TouchableOpacity, Modal, Platform, StatusBar,
} from 'react-native';
import { Gesture, GestureDetector, GestureHandlerRootView } from 'react-native-gesture-handler';
import { initialWindowMetrics, useSafeAreaInsets } from 'react-native-safe-area-context';
import Animated, {
  runOnJS, useAnimatedStyle, useSharedValue, withTiming,
} from 'react-native-reanimated';
import Colors from '../../../../../../constants/Colors';
import CloseModalIcon from '../../../../../CloseModalIcon';
import Style from '../../../../../../constants/Style';
import DotsRow from '../../DotsRow';
import Button from '../../../../../Button';
import { TemplateIssue } from '../../../../../../constants/Types';

const ROW_HEIGHT = 64;
const ROW_GAP = 12;
const ROW_SIZE = ROW_HEIGHT + ROW_GAP;

const getIssueKey = (issue: TemplateIssue, index: number) => `${issue.Id ?? issue.LocalId ?? `issue_${index}`}`;

interface IssueReorderModalProps {
  issues: TemplateIssue[],
  setIssues: Dispatch<SetStateAction<TemplateIssue[]>>,
  hide: () => void,
  visible: boolean,
}

interface IssueItemProps {
  rowKey: string,
  title: string,
  index: number,
  count: number,
  // eslint-disable-next-line no-unused-vars
  moveIssue: (rowKey: string, toIndex: number) => void,
}

const IssueItem = ({
  rowKey, title, index, count, moveIssue,
}: IssueItemProps) => {
  const [dragging, setDragging] = useState<boolean>(false);
  const translateY = useSharedValue(index * ROW_SIZE);
  const startY = useSharedValue(0);
  const currentIndex = useSharedValue(index);

  useEffect(() => {
    currentIndex.value = index;
    if (!dragging) {
      translateY.value = withTiming(index * ROW_SIZE, { duration: 140 });
    }
  }, [index, dragging]);

  const panGesture = Gesture.Pan()
    .activateAfterLongPress(150)
    .onBegin(() => {
      runOnJS(setDragging)(true);
      startY.value = translateY.value;
    })
    .onUpdate((event) => {
      translateY.value = startY.value + event.translationY;

      const nextIndex = Math.max(
        0,
        Math.min(
          Math.round(translateY.value / ROW_SIZE),
          Math.max(count - 1, 0),
        ),
      );

      if (nextIndex !== currentIndex.value) {
        currentIndex.value = nextIndex;
        runOnJS(moveIssue)(rowKey, nextIndex);
      }
    })
    .onFinalize(() => {
      runOnJS(setDragging)(false);
      translateY.value = withTiming(currentIndex.value * ROW_SIZE, { duration: 140 });
    });

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
    zIndex: dragging ? 20 : 1,
  }));

  return <GestureDetector gesture={panGesture}>
      <Animated.View
        style={[
          styles.issueItemWrapper,
          animatedStyle,
        ]}
      >
        <View style={[styles.issueItem, dragging ? styles.draggingStyle : styles.receivingStyle]}>
          <View style={Style.row}>
            <Text style={styles.issueTitle}>
              {title?.length ? title : 'New issue'}
            </Text>
          </View>
          <DotsRow />
        </View>
      </Animated.View>
    </GestureDetector>;
};

const IssueReorderModal = ({
  issues, hide, visible, setIssues,
}: IssueReorderModalProps) => {
  const [issueList, setIssueList] = useState<TemplateIssue[]>(issues);

  const insets = useSafeAreaInsets();
  const modalTopInset = Platform.OS === 'android' ? Math.max(
    insets.top,
    initialWindowMetrics?.insets.top ?? 0,
    StatusBar.currentHeight ?? 0,
    32,
  ) : insets.top;
  const modalBottomInset = Platform.OS === 'android' ? Math.max(
    insets.bottom,
    initialWindowMetrics?.insets.bottom ?? 0,
    16,
  ) : insets.bottom;

  useEffect(() => setIssueList(issues), [visible]);

  const filteredIssues = useMemo(
    () => issueList?.filter((issue) => issue.Text.toLowerCase() !== 'other'),
    [issueList],
  );

  const moveIssue = useCallback((rowKey: string, toIndex: number) => {
    setIssueList((prevIssues) => {
      const withoutOther = prevIssues.filter((issue) => issue.Text.toLowerCase() !== 'other');
      const fromIndex = withoutOther.findIndex(
        (issue, index) => getIssueKey(issue, index) === rowKey,
      );

      if (fromIndex === -1 || fromIndex === toIndex) return prevIssues;

      const nextIssues = [...withoutOther];
      const [movedIssue] = nextIssues.splice(fromIndex, 1);
      if (!movedIssue) return prevIssues;

      nextIssues.splice(toIndex, 0, movedIssue);
      return nextIssues;
    });
  }, []);

  return <Modal
    visible={visible}
    onRequestClose={hide}
    animationType='fade'
    transparent={false}
    statusBarTranslucent={false}
  >
    <GestureHandlerRootView style={styles.stretch}>
    <View
      style={[
        styles.container,
        {
          paddingTop: modalTopInset + 12,
          paddingBottom: modalBottomInset + 16,
        },
      ]}
    >
      <TouchableOpacity
        style={[Style.closeButton, { top: 12 + modalTopInset }]}
        onPress={hide}
      >
        <CloseModalIcon />
      </TouchableOpacity>
      <>
        <Text style={styles.title}>Reorder Issues</Text>
        <View style={[styles.stretch, { minHeight: filteredIssues.length * ROW_SIZE }]}>
          {filteredIssues.map((item, index) => (
            <IssueItem
              key={getIssueKey(item, index)}
              rowKey={getIssueKey(item, index)}
              title={item.Text}
              index={index}
              count={filteredIssues.length}
              moveIssue={moveIssue}
            />
          ))}
        </View>
      </>
      <View style={styles.buttonContainer}>
        <Button
          title='Cancel'
          onPress={hide}
        />
        <Button
          title='Save'
          affirmative
          onPress={() => {
            setIssues(issueList);
            hide();
          }}
        />
      </View>
    </View>
    </GestureHandlerRootView>
  </Modal>;
};

const styles = StyleSheet.create({
  container: {
    height: '100%',
    width: '100%',
    backgroundColor: '#FFF',
    alignItems: 'center',
    paddingTop: 60,
  },
  title: {
    fontSize: 28,
    lineHeight: 35,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    textAlign: 'center',
    marginBottom: 32,
    alignSelf: 'center',
  },
  issueItem: {
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 16,
    height: ROW_HEIGHT,
    justifyContent: 'center',
    backgroundColor: Colors.light.hereworksWhite200,
  },
  issueItemWrapper: {
    position: 'absolute',
    left: 24,
    right: 24,
    top: 0,
  },
  issueTitle: {
    fontSize: 16,
    lineHeight: 24,
    paddingHorizontal: 6,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
  },
  receivingStyle: {
    backgroundColor: Colors.light.hereworksBlack50,
    borderRadius: 12,
  },
  draggingStyle: {
    backgroundColor: Colors.light.hereworksBlack100,
    borderRadius: 12,
  },
  stretch: {
    alignSelf: 'stretch',
    flex: 1,
  },
  buttonContainer: {
    flexDirection: 'row',
    paddingHorizontal: 24,
  },
});

export default IssueReorderModal;
